import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Search, User, Wallet, ArrowRight, AlertCircle, Info, ChevronRight,
  Paperclip, Upload, FileText, X, MessageCircle, CheckCircle2,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { MAX_COMPROBANTES, MAX_FILE_MB, isPdfUrl, formatCurrency, formatDate } from './utils';
import api, { createAbonoCliente, deleteAbonoGrupo } from '../../lib/api';
import { notifyAbonoOnWhatsApp } from '../../lib/whatsapp';

/**
 * Registrar Pago — Diálogo POR CLIENTE.
 * Reemplaza al flujo anterior por-factura: ahora el usuario busca al cliente,
 * ve TODAS sus facturas pendientes con saldo, y digita el "Monto a pagar" que
 * se distribuye automáticamente FIFO (más viejas primero).
 *
 * Props:
 *  - open, onOpenChange
 *  - clientes: lista completa
 *  - initialClienteId: (opcional) pre-selecciona un cliente
 *  - comprobantes/onSelectComprobantes/... (opcional; solo si se quiere adjuntar)
 *  - onSaved: callback tras registrar el pago exitosamente
 */
export const PagoDialog = ({
  open,
  onOpenChange,
  clientes = [],
  initialClienteId = null,
  comprobantes = [],
  onSelectComprobantes,
  onRemoveComprobante,
  uploadingComprobante = false,
  onSaved,
  // Modo edición: si se pasa un abono existente, precarga sus campos y al
  // guardar reemplaza (delete + create) el abono anterior.
  editingAbono = null,
  onSetComprobantes,
}) => {
  const isEdit = !!editingAbono;
  const [busqueda, setBusqueda] = useState('');
  const [clienteId, setClienteId] = useState(initialClienteId);
  const [estadoCuenta, setEstadoCuenta] = useState(null);
  const [loading, setLoading] = useState(false);

  const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0]);
  const [monto, setMonto] = useState('');
  const [metodo, setMetodo] = useState('Efectivo');
  const [referencia, setReferencia] = useState('');
  const [descripcion, setDescripcion] = useState('');
  // Multi-moneda
  const [moneda, setMoneda] = useState('DOP'); // 'DOP' | 'USD'
  const [montoUsd, setMontoUsd] = useState('');
  const [tasaCambio, setTasaCambio] = useState(() => {
    try { return localStorage.getItem('cxc_last_tasa_usd') || ''; } catch (_) { return ''; }
  });
  const [saving, setSaving] = useState(false);
  const [resultado, setResultado] = useState(null); // { abono, cliente } tras registrar

  // Reset SOLO cuando el modal se abre. En modo edición, precargamos con los
  // valores del abono en vez de resetear a valores en blanco.
  useEffect(() => {
    if (!open) return;
    if (editingAbono) {
      // Precarga desde el grupo/abono a editar
      setBusqueda(editingAbono.cliente_nombre || '');
      setClienteId(editingAbono.cliente_id || null);
      setFecha((editingAbono.fecha || new Date().toISOString().split('T')[0]).slice(0, 10));
      const totalDop = (editingAbono.items || []).reduce((s, p) => s + Number(p.monto || 0), 0);
      const mnd = (editingAbono.moneda || 'DOP').toUpperCase();
      setMoneda(mnd);
      setMetodo(editingAbono.metodo_pago || 'Efectivo');
      setReferencia(editingAbono.referencia || '');
      setDescripcion(editingAbono.descripcion || '');
      if (mnd === 'USD') {
        setMonto('');
        setMontoUsd(String(editingAbono.monto_original || ''));
        if (editingAbono.tasa) setTasaCambio(String(editingAbono.tasa));
      } else {
        setMonto(String(+totalDop.toFixed(2)));
        setMontoUsd('');
      }
      // Precargar comprobantes del abono (si el padre nos deja setear)
      if (typeof onSetComprobantes === 'function') {
        const comps = editingAbono.comprobantes || [];
        onSetComprobantes(Array.isArray(comps) ? comps : []);
      }
      setResultado(null);
    } else {
      setBusqueda('');
      setClienteId(initialClienteId);
      setEstadoCuenta(null);
      setFecha(new Date().toISOString().split('T')[0]);
      setMonto('');
      setMetodo('Efectivo');
      setReferencia('');
      setDescripcion('');
      setMoneda('DOP');
      setMontoUsd('');
      // conservar tasaCambio para la próxima
      setResultado(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Cargar estado de cuenta cuando cambia el cliente
  const cargarEstado = useCallback(async (id) => {
    if (!id) { setEstadoCuenta(null); return; }
    setLoading(true);
    try {
      const res = await api.get(`/cxc/estado-cuenta/${id}`);
      setEstadoCuenta(res.data);
    } catch (_) {
      toast.error('No se pudo cargar el estado del cliente');
      setEstadoCuenta(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarEstado(clienteId);
  }, [clienteId, cargarEstado]);

  // Sugerencias autocomplete
  const sugerencias = useMemo(() => {
    const term = busqueda.trim().toLowerCase();
    if (!term) return [];
    return clientes
      .filter((c) => {
        const n = (c.nombre || '').toLowerCase();
        const t = (c.telefono || '').toLowerCase();
        const e = (c.email || '').toLowerCase();
        return n.includes(term) || t.includes(term) || e.includes(term);
      })
      .slice(0, 8);
  }, [busqueda, clientes]);

  const cliente = estadoCuenta?.cliente || null;
  const facturasPendientes = useMemo(() => {
    if (!estadoCuenta?.facturas) return [];
    return estadoCuenta.facturas
      .filter((f) => ['Pendiente', 'Parcial'].includes(f.estado_pago))
      .sort((a, b) => {
        const df = (a.fecha || '').localeCompare(b.fecha || '');
        if (df !== 0) return df;
        return (a.numero_factura || '').localeCompare(b.numero_factura || '', 'es', { numeric: true });
      });
  }, [estadoCuenta]);

  const saldoTotalCliente = useMemo(
    () => facturasPendientes.reduce((acc, f) => acc + Number(f.saldo || 0), 0),
    [facturasPendientes],
  );

  // Calcular monto DOP efectivo (si USD, monto_usd * tasa)
  const montoUsdNum = parseFloat(montoUsd) || 0;
  const tasaNum = parseFloat(tasaCambio) || 0;
  const montoNum = moneda === 'USD'
    ? +(montoUsdNum * tasaNum).toFixed(2)
    : parseFloat(monto) || 0;
  const excedeSaldo = montoNum > saldoTotalCliente + 0.01;

  // Simulación FIFO para vista previa
  const preview = useMemo(() => {
    if (montoNum <= 0) return { aplicaciones: [] };
    let restante = montoNum;
    const list = [];
    for (const f of facturasPendientes) {
      if (restante <= 0.005) break;
      const saldo = Number(f.saldo || 0);
      if (saldo <= 0) continue;
      const aplicar = Math.min(restante, saldo);
      const nuevoSaldo = Math.max(0, +(saldo - aplicar).toFixed(2));
      const nuevoEstado = nuevoSaldo <= 0.01
        ? 'Pagada'
        : (nuevoSaldo < Number(f.total_monto || 0) ? 'Parcial' : 'Pendiente');
      list.push({
        factura_id: f.id,
        factura_numero: f.numero_factura,
        fecha: f.fecha,
        saldo_previo: saldo,
        monto_aplicado: +aplicar.toFixed(2),
        saldo_restante: nuevoSaldo,
        estado_pago_nuevo: nuevoEstado,
      });
      restante = +(restante - aplicar).toFixed(2);
    }
    return { aplicaciones: list };
  }, [montoNum, facturasPendientes]);

  const saldoRestanteCliente = Math.max(0, +(saldoTotalCliente - montoNum).toFixed(2));
  const monedaValida = moneda === 'DOP'
    ? true
    : (montoUsdNum > 0 && tasaNum > 0);
  const puedeRegistrar = clienteId && montoNum > 0 && !excedeSaldo && preview.aplicaciones.length > 0 && !saving && !uploadingComprobante && monedaValida;

  const seleccionar = (c) => {
    setClienteId(c.id || c._id);
    setBusqueda(c.nombre || '');
  };
  const limpiarSeleccion = () => {
    setClienteId(null);
    setBusqueda('');
    setEstadoCuenta(null);
  };

  const handleRegistrar = async () => {
    if (!puedeRegistrar) return;
    setSaving(true);
    try {
      // Modo EDICIÓN: eliminar primero el abono anterior para restaurar los
      // saldos, y luego crear el nuevo con los datos actualizados.
      if (isEdit && editingAbono?.grupo_id) {
        try {
          await deleteAbonoGrupo(editingAbono.grupo_id);
        } catch (delErr) {
          const dd = delErr.response?.data?.detail;
          toast.error(typeof dd === 'string' ? dd : 'No se pudo revertir el abono anterior');
          setSaving(false);
          return;
        }
      }
      const payload = {
        cliente_id: clienteId,
        fecha,
        monto: +montoNum.toFixed(2),
        metodo_pago: metodo,
        referencia: referencia.trim() || null,
        descripcion: descripcion.trim() || null,
        comprobantes: comprobantes || [],
        moneda,
      };
      if (moneda === 'USD') {
        payload.tasa_cambio = +tasaNum.toFixed(4);
        payload.monto_original = +montoUsdNum.toFixed(2);
        try { localStorage.setItem('cxc_last_tasa_usd', String(tasaNum)); } catch (_) { /* noop */ }
      }
      const res = await createAbonoCliente(payload);
      const data = res.data;
      toast.success(
        isEdit
          ? `Pago actualizado (${formatCurrency(data.monto_aplicado)} — ${data.aplicaciones.length} factura(s))`
          : `Pago de ${formatCurrency(data.monto_aplicado)} registrado — ${data.aplicaciones.length} factura(s) actualizada(s)`,
      );
      setResultado({ abono: data, cliente: estadoCuenta?.cliente });
      if (typeof onSaved === 'function') onSaved(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al registrar el pago');
    } finally {
      setSaving(false);
    }
  };

  const handleEnviarWhatsApp = () => {
    if (!resultado) return;
    const ok = notifyAbonoOnWhatsApp({
      cliente: resultado.cliente,
      abono: resultado.abono,
    });
    if (!ok) {
      toast.error('El teléfono del cliente no es válido para WhatsApp');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-6xl w-[95vw] max-h-[92vh] overflow-y-auto"
        data-testid="modal-registrar-pago"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            {resultado ? (
              <>
                <CheckCircle2 className="w-5 h-5 text-emerald-600" /> {isEdit ? 'Pago Actualizado' : 'Pago Registrado'}
              </>
            ) : (
              <>
                <Wallet className="w-5 h-5" /> {isEdit ? 'Editar Pago' : 'Registrar Pago'}
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        {resultado ? (
          /* --------- PASO DE ÉXITO --------- */
          <div className="space-y-4 py-2" data-testid="paso-exito-pago">
            <div className="rounded-lg border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20 p-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-8 h-8 text-emerald-600 shrink-0" />
                <div className="flex-1">
                  <div className="text-lg font-semibold text-emerald-900 dark:text-emerald-100" style={{ fontFamily: 'Space Grotesk' }}>
                    {formatCurrency(resultado.abono.monto_aplicado)} aplicado a {resultado.abono.aplicaciones.length} factura{resultado.abono.aplicaciones.length === 1 ? '' : 's'}
                  </div>
                  <div className="text-sm text-emerald-800 dark:text-emerald-300 mt-0.5">
                    Cliente: <b>{resultado.cliente?.nombre}</b> · Método: {resultado.abono.metodo_pago}
                  </div>
                </div>
              </div>

              <div className="mt-3 rounded-md bg-background/70 border border-emerald-200/60 overflow-hidden">
                {resultado.abono.aplicaciones.map((a) => (
                  <div key={a.factura_id} className="flex items-center justify-between px-3 py-1.5 text-xs border-b border-emerald-100 last:border-0">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-[10px]">{a.factura_numero}</Badge>
                      <span className="text-muted-foreground">{formatDate(a.fecha_factura)}</span>
                    </div>
                    <div className="tabular-nums flex items-center gap-1.5">
                      <span className="font-semibold text-emerald-700">+{formatCurrency(a.monto_aplicado)}</span>
                      <ArrowRight className="w-3 h-3 text-muted-foreground" />
                      <span>{a.estado_pago_nuevo === 'Pagada' ? 'Saldo 0' : formatCurrency(a.saldo_restante)}</span>
                      <Badge
                        variant={a.estado_pago_nuevo === 'Pagada' ? 'default' : 'secondary'}
                        className="text-[9px] px-1 py-0 h-4 ml-1"
                      >
                        {a.estado_pago_nuevo}
                      </Badge>
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-between px-3 py-2 bg-muted/50 text-xs font-semibold">
                  <span>Saldo pendiente del cliente</span>
                  <span className="tabular-nums">{formatCurrency(resultado.abono.saldo_total_restante_cliente)}</span>
                </div>
              </div>
            </div>

            {/* Notificar por WhatsApp */}
            <div className="rounded-lg border p-4 bg-accent/30">
              <div className="flex items-start gap-3">
                <MessageCircle className="w-6 h-6 text-[#25D366] shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-sm">Notificar al cliente por WhatsApp</div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Se abrirá WhatsApp con el mensaje listo. Solo tendrás que tocar &ldquo;Enviar&rdquo;.
                  </p>
                  {(resultado.cliente?.telefono || resultado.cliente?.celular) ? (
                    <Button
                      onClick={handleEnviarWhatsApp}
                      className="mt-3 bg-[#25D366] hover:bg-[#1DA851] text-white gap-2"
                      data-testid="btn-enviar-whatsapp"
                    >
                      <MessageCircle className="w-4 h-4" />
                      Enviar por WhatsApp ({resultado.cliente.telefono || resultado.cliente.celular})
                    </Button>
                  ) : (
                    <div className="mt-2 flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400">
                      <AlertCircle className="w-3 h-3" />
                      Este cliente no tiene teléfono registrado.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
        /* --------- FORMULARIO --------- */
        <div className="space-y-4 py-2">
          {/* Búsqueda de cliente */}
          <div>
            <Label>Cliente *</Label>
            <div className="relative mt-1.5">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Escribe el nombre, teléfono o email del cliente..."
                value={busqueda}
                onChange={(e) => { setBusqueda(e.target.value); if (clienteId) setClienteId(null); }}
                className="h-11 pl-9"
                data-testid="input-busqueda-cliente-pago"
                autoFocus={!clienteId}
              />
              {sugerencias.length > 0 && !clienteId && (
                <div className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-md border bg-popover shadow-lg">
                  {sugerencias.map((c) => (
                    <button
                      key={c.id || c._id}
                      type="button"
                      onClick={() => seleccionar(c)}
                      className="w-full text-left px-3 py-2 hover:bg-accent transition-colors flex items-center gap-2 border-b border-border/40 last:border-0"
                      data-testid={`sugerencia-pago-${c.id || c._id}`}
                    >
                      <User className="w-4 h-4 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{c.nombre}</div>
                        {(c.telefono || c.email) && (
                          <div className="text-[11px] text-muted-foreground truncate">
                            {[c.telefono, c.email].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              )}
            </div>
            {clienteId && (
              <div className="mt-2 flex items-center gap-2 text-xs">
                <Badge variant="secondary" className="gap-1">
                  <User className="w-3 h-3" /> {cliente?.nombre || busqueda}
                </Badge>
                <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={limpiarSeleccion}>
                  cambiar cliente
                </Button>
              </div>
            )}
          </div>

          {/* Contenido cuando hay cliente */}
          {loading && clienteId && (
            <div className="text-center py-6 text-muted-foreground text-sm">Cargando…</div>
          )}

          {!loading && clienteId && estadoCuenta && (
            <>
              {/* Tabla de facturas pendientes con saldo/pendiente */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Facturas del cliente
                  </div>
                  <Badge variant="outline" className="text-[10px]">Más viejas primero (FIFO)</Badge>
                </div>
                {facturasPendientes.length === 0 ? (
                  <div className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
                    Este cliente no tiene facturas pendientes.
                  </div>
                ) : (
                  <div className="rounded-md border overflow-x-auto max-h-64 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-8">#</TableHead>
                          <TableHead>Factura</TableHead>
                          <TableHead>Fecha</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead className="text-right">Abonado</TableHead>
                          <TableHead className="text-right">Pendiente</TableHead>
                          <TableHead className="text-center">Estado</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {facturasPendientes.map((f, idx) => {
                          const total = Number(f.total_monto || 0);
                          const saldo = Number(f.saldo || 0);
                          const abonado = Math.max(0, total - saldo);
                          return (
                            <TableRow key={f.id} data-testid={`pago-fact-${f.id}`}>
                              <TableCell className="text-muted-foreground text-xs">{idx + 1}</TableCell>
                              <TableCell className="font-semibold">{f.numero_factura}</TableCell>
                              <TableCell className="text-xs">{formatDate(f.fecha)}</TableCell>
                              <TableCell className="text-right tabular-nums">{formatCurrency(total)}</TableCell>
                              <TableCell className="text-right tabular-nums text-emerald-700 dark:text-emerald-400">
                                {formatCurrency(abonado)}
                              </TableCell>
                              <TableCell className="text-right tabular-nums font-semibold text-destructive">
                                {formatCurrency(saldo)}
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge
                                  variant={f.estado_pago === 'Parcial' ? 'secondary' : 'outline'}
                                  className="text-[10px]"
                                >
                                  {f.estado_pago}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>

              {/* Balance total */}
              <div className="rounded-lg border bg-primary/5 border-primary/20 p-3 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
                    Balance total a deber
                  </div>
                  <div
                    className="text-2xl font-bold tabular-nums text-primary"
                    style={{ fontFamily: 'Space Grotesk' }}
                    data-testid="balance-total-cliente"
                  >
                    {formatCurrency(saldoTotalCliente)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
                    Facturas pendientes
                  </div>
                  <div
                    className="text-2xl font-bold tabular-nums"
                    style={{ fontFamily: 'Space Grotesk' }}
                  >
                    {facturasPendientes.length}
                  </div>
                </div>
              </div>

              <Separator />

              {/* Formulario del pago */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label>Fecha *</Label>
                  <Input
                    type="date"
                    value={fecha}
                    onChange={(e) => setFecha(e.target.value)}
                    className="h-11 mt-1.5"
                    data-testid="input-fecha-pago"
                  />
                </div>
                <div>
                  <Label>Moneda *</Label>
                  <Select value={moneda} onValueChange={(v) => { setMoneda(v); if (v === 'DOP') { setMontoUsd(''); } else { setMonto(''); } }}>
                    <SelectTrigger className="h-11 mt-1.5" data-testid="select-moneda-pago">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DOP">DOP · Pesos dominicanos</SelectItem>
                      <SelectItem value="USD">USD · Dólares</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {moneda === 'DOP' ? (
                <div>
                  <Label>Monto a pagar (DOP) *</Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    max={saldoTotalCliente}
                    placeholder="0.00"
                    value={monto}
                    onChange={(e) => setMonto(e.target.value)}
                    className={`h-11 mt-1.5 text-lg tabular-nums font-semibold ${
                      excedeSaldo ? 'border-red-400 focus-visible:ring-red-400' : ''
                    }`}
                    data-testid="input-monto-pago"
                  />
                  {excedeSaldo && (
                    <div className="mt-1 flex items-center gap-1 text-[11px] text-red-600">
                      <AlertCircle className="w-3 h-3" />
                      Excede el balance total ({formatCurrency(saldoTotalCliente)})
                    </div>
                  )}
                  {saldoTotalCliente > 0 && (
                    <div className="mt-1 flex items-center gap-1 text-[10px] flex-wrap">
                      <button
                        type="button"
                        onClick={() => setMonto(saldoTotalCliente.toFixed(2))}
                        className="px-1.5 py-0.5 rounded border border-primary/40 bg-primary/10 hover:bg-primary/20 font-medium"
                        data-testid="btn-monto-total"
                      >
                        Balance completo
                      </button>
                      <button
                        type="button"
                        onClick={() => setMonto((saldoTotalCliente * 0.5).toFixed(2))}
                        className="px-1.5 py-0.5 rounded border hover:bg-accent text-muted-foreground hover:text-foreground"
                      >
                        50%
                      </button>
                      {facturasPendientes[0] && (
                        <button
                          type="button"
                          onClick={() => setMonto(Number(facturasPendientes[0].saldo || 0).toFixed(2))}
                          className="px-1.5 py-0.5 rounded border hover:bg-accent text-muted-foreground hover:text-foreground"
                        >
                          Cubrir la más vieja
                        </button>
                      )}
                    </div>
                  )}
                </div>
                ) : (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Monto USD *</Label>
                    <Input
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={montoUsd}
                      onChange={(e) => setMontoUsd(e.target.value)}
                      className="h-11 mt-1.5 text-lg tabular-nums font-semibold"
                      data-testid="input-monto-usd"
                    />
                  </div>
                  <div>
                    <Label>Tasa (DOP × 1 USD) *</Label>
                    <Input
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min="0"
                      placeholder="62.50"
                      value={tasaCambio}
                      onChange={(e) => setTasaCambio(e.target.value)}
                      className="h-11 mt-1.5 text-lg tabular-nums font-semibold"
                      data-testid="input-tasa-cambio"
                    />
                  </div>
                  <div className="col-span-2">
                    <div className={`p-2 rounded-md border text-xs ${excedeSaldo ? 'border-red-400 bg-red-50 text-red-700' : 'border-primary/30 bg-primary/5'}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Equivalente DOP:</span>
                        <span className="font-semibold tabular-nums text-sm" data-testid="txt-equivalente-dop">
                          {formatCurrency(montoNum)}
                        </span>
                      </div>
                      {excedeSaldo && (
                        <div className="mt-1 flex items-center gap-1 text-[11px]">
                          <AlertCircle className="w-3 h-3" /> Excede el balance ({formatCurrency(saldoTotalCliente)})
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Método de Pago *</Label>
                  <Select value={metodo} onValueChange={setMetodo}>
                    <SelectTrigger className="h-11 mt-1.5" data-testid="select-metodo-pago">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Efectivo">Efectivo</SelectItem>
                      <SelectItem value="Transferencia">Transferencia</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Referencia (opcional)</Label>
                  <Input
                    placeholder="Ej: #TRX-982, cheque..."
                    value={referencia}
                    onChange={(e) => setReferencia(e.target.value)}
                    className="h-11 mt-1.5"
                    data-testid="input-referencia-pago"
                  />
                </div>
              </div>

              <div>
                <Label>Descripción (opcional)</Label>
                <textarea
                  rows={2}
                  placeholder="Notas sobre este pago..."
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  className="w-full mt-1.5 px-3 py-2 text-sm border rounded-md bg-background min-h-[60px]"
                  data-testid="input-observaciones-pago"
                />
              </div>

              {/* Comprobantes (opcional) */}
              {onSelectComprobantes && (
                <div className="border-t pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="flex items-center gap-2">
                      <Paperclip className="w-4 h-4" />
                      Comprobantes ({comprobantes.length}/{MAX_COMPROBANTES})
                    </Label>
                    <span className="text-[10px] text-muted-foreground">
                      JPG, PNG, PDF · máx. {MAX_FILE_MB} MB c/u
                    </span>
                  </div>
                  {comprobantes.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
                      {comprobantes.map((c, idx) => {
                        const isPdf = (c.content_type || '').includes('pdf') || isPdfUrl(c.url);
                        return (
                          <div key={c.url || idx} className="relative border rounded-md overflow-hidden bg-muted/40">
                            {isPdf ? (
                              <div className="h-20 flex flex-col items-center justify-center text-muted-foreground">
                                <FileText className="w-6 h-6" />
                                <span className="text-[10px] mt-1 px-1 truncate w-full text-center">
                                  {c.filename || 'PDF'}
                                </span>
                              </div>
                            ) : (
                              <a href={c.url} target="_blank" rel="noopener noreferrer">
                                <img src={c.url} alt={c.filename || `Comp ${idx + 1}`} className="h-20 w-full object-cover" loading="lazy" />
                              </a>
                            )}
                            <button
                              type="button"
                              onClick={() => onRemoveComprobante && onRemoveComprobante(idx)}
                              disabled={uploadingComprobante || saving}
                              className="absolute top-1 right-1 bg-black/60 hover:bg-destructive text-white rounded-full p-1"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {comprobantes.length < MAX_COMPROBANTES && (
                    <label
                      className={`inline-flex items-center gap-2 h-9 px-3 rounded-md border border-dashed cursor-pointer text-xs transition-colors ${
                        uploadingComprobante ? 'opacity-50 pointer-events-none' : 'hover:bg-accent'
                      }`}
                    >
                      <Upload className="w-3.5 h-3.5" />
                      {uploadingComprobante ? 'Subiendo...' : 'Adjuntar comprobante'}
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,application/pdf"
                        multiple
                        className="hidden"
                        onChange={onSelectComprobantes}
                        disabled={uploadingComprobante}
                      />
                    </label>
                  )}
                </div>
              )}

              {/* Preview de distribución FIFO */}
              {montoNum > 0 && preview.aplicaciones.length > 0 && (
                <div className="border-t pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                      <Info className="w-3 h-3" /> Vista previa · así se aplicará
                    </div>
                    <Badge variant="outline" className="text-[10px]">
                      Cubrirá {preview.aplicaciones.length} factura{preview.aplicaciones.length === 1 ? '' : 's'}
                    </Badge>
                  </div>
                  <div className="rounded-md border overflow-hidden">
                    {preview.aplicaciones.map((a) => (
                      <div
                        key={a.factura_id}
                        className="flex items-center justify-between px-3 py-2 text-xs border-b border-border/40 last:border-0 bg-background hover:bg-accent/30 transition-colors"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Badge variant="secondary" className="text-[10px] shrink-0">{a.factura_numero}</Badge>
                          <span className="text-muted-foreground truncate">
                            {formatDate(a.fecha)} · saldo actual {formatCurrency(a.saldo_previo)}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0 tabular-nums">
                          <span className="font-semibold text-emerald-700">+{formatCurrency(a.monto_aplicado)}</span>
                          <ArrowRight className="w-3 h-3 text-muted-foreground" />
                          <span className={a.estado_pago_nuevo === 'Pagada' ? 'text-emerald-700 font-semibold' : ''}>
                            {a.estado_pago_nuevo === 'Pagada' ? 'Saldo 0' : formatCurrency(a.saldo_restante)}
                          </span>
                          <Badge
                            variant={a.estado_pago_nuevo === 'Pagada' ? 'default' : 'secondary'}
                            className="text-[9px] px-1 py-0 h-4 ml-1"
                          >
                            {a.estado_pago_nuevo}
                          </Badge>
                        </div>
                      </div>
                    ))}
                    <div className="flex items-center justify-between px-3 py-2 bg-muted/50 text-xs font-semibold">
                      <span>Balance del cliente después del pago</span>
                      <span className="tabular-nums">{formatCurrency(saldoRestanteCliente)}</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {!clienteId && !busqueda && (
            <div className="rounded-md border border-dashed py-10 text-center text-muted-foreground">
              <User className="w-10 h-10 mx-auto opacity-30 mb-2" />
              <p className="text-sm">Empieza escribiendo el nombre del cliente para ver sus facturas.</p>
            </div>
          )}
        </div>
        )}

        <DialogFooter>
          {resultado ? (
            <Button onClick={() => onOpenChange(false)} data-testid="btn-cerrar-pago-exito">
              Cerrar
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={saving || uploadingComprobante}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleRegistrar}
                disabled={!puedeRegistrar}
                data-testid="btn-confirmar-pago"
              >
                {saving ? (isEdit ? 'Guardando...' : 'Registrando...') : (montoNum > 0 ? `${isEdit ? 'Guardar' : 'Registrar'} ${formatCurrency(montoNum)}` : (isEdit ? 'Guardar' : 'Registrar'))}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
